import React, { FC, useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "constant/constant";
import { useAppContext } from "AppContext";
import { toFilterDateString } from "utils/filterDates";
import bedIcon from "images/card/bed-icon.svg";
import bathIcon from "images/card/bath-icon.svg";
import kitchenIcon from "images/card/kitchen-icon.svg";
import sleepsIcon from "images/card/sleeps-icon.svg";
import pinIcon from "images/card/pin-icon.svg";
import heartFilledIcon from "images/card/heart-filled-icon.svg";
import internetIcon from "images/filters/amenities/internet.svg";
import swimmingPoolIcon from "images/filters/amenities/swimming-pool.svg";
import parkingIcon from "images/filters/amenities/parking.svg";
import washingMachineIcon from "images/filters/amenities/washing-machine.svg";
import heatingIcon from "images/filters/amenities/heating.svg";
import airConditioningIcon from "images/filters/amenities/air-conditioning.svg";
import gardenIcon from "images/filters/amenities/garden.svg";
import terraceIcon from "images/filters/amenities/terrace.svg";
import getImageVariantUrl from "utils/getImageVariantUrl";
import { useLocale } from "i18n/locale";

export interface MapPropertyCardProps {
  data: any;
  onClose: () => void;
}

const AMENITY_ICON_MAP: Record<string, string> = {
  "internet": internetIcon,
  "swimming pool": swimmingPoolIcon,
  "parking": parkingIcon,
  "washing machine": washingMachineIcon,
  "heating": heatingIcon,
  "air conditioning": airConditioningIcon,
  "garden": gardenIcon,
  "terrace": terraceIcon,
};

const AMENITY_PRIORITY = [
  "internet",
  "swimming pool",
  "garden",
  "terrace",
  "parking",
  "air conditioning",
  "heating",
  "washing machine",
];

const getAmenityIcon = (name: string): string | null => {
  const key = name.toLowerCase().trim();
  for (const [mapKey, icon] of Object.entries(AMENITY_ICON_MAP)) {
    if (key.includes(mapKey)) return icon;
  }
  return null;
};

const getAmenityPriority = (name: string): number => {
  const key = name.toLowerCase().trim();
  for (let i = 0; i < AMENITY_PRIORITY.length; i++) {
    if (key.includes(AMENITY_PRIORITY[i])) return i;
  }
  return AMENITY_PRIORITY.length;
};


const MapPropertyCard: FC<MapPropertyCardProps> = ({ data, onClose }) => {
  const { t } = useLocale();
  const { filterState } = useAppContext();

  const {
    _id,
    title,
    base_price,
    avgRatePerDay,
    bedrooms,
    bathrooms,
    toilets,
    sleeps,
    location,
    city,
    type_of_unit,
    status_of_unit,
    amenities,
    galleryImgs,
  } = data;

  const [descriptionText, setDescriptionText] = useState<string>("");

  useEffect(() => {
    if (!_id) return;
    axios
      .get(`${base_url}/property/website_property_details`, { params: { property_id: _id } })
      .then((res) => {
        const desc = res.data?.data?.description?.en;
        if (desc) setDescriptionText(desc);
      })
      .catch(() => {});
  }, [_id]);

  const price = avgRatePerDay ? Math.round(avgRatePerDay) : base_price;
  const displayLocation = location || city || "";
  const image = galleryImgs?.[0];

  // residence_type is legacy and only set on older properties — the live fields
  // are type_of_unit / status_of_unit (same pair PropertyCardEuropea uses).
  const badges: string[] = [];
  if (type_of_unit) badges.push(type_of_unit.replace(/-/g, " "));
  if (status_of_unit) badges.push(status_of_unit.replace(/-/g, " "));

  const amenityList: { name: string }[] = (amenities || []).filter(
    (a: any) => a && typeof a === "object" && a.name && a.name.trim()
  );
  const amenitiesWithIcons = amenityList
    .filter((a) => getAmenityIcon(a.name))
    .sort((a, b) => getAmenityPriority(a.name) - getAmenityPriority(b.name));
  const visibleAmenities = amenitiesWithIcons.slice(0, 2);
  const moreCount = amenityList.length - visibleAmenities.length;

  const startParam = toFilterDateString(filterState?.checkInDate) ?? "";
  const endParam = toFilterDateString(filterState?.checkOutDate) ?? "";
  const detailUrl = `/listing-stay-detail?id=${_id}&start_date=${startParam}&number_of_guests=${filterState?.guestNumber}&end_date=${endParam}&number_of_children=${filterState?.number_of_children}&number_of_infants=${filterState?.number_of_infants}`;

  const handleBookNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(detailUrl, "_blank");
  };

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const original = img.dataset.original || "";
    // Prefer the _compress variant (full resolution, lighter JPEG); if it is
    // missing, fall back to the full original.
    if (original && img.src !== original) {
      img.src = original;
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: 6,
        width: "100%",
        maxWidth: 340,
        padding: 20,
        boxShadow: "0px 0px 6px rgba(42,54,84,0.3)",
        cursor: "pointer",
      }}
      onClick={(e) => {
        e.stopPropagation();
        window.open(detailUrl, "_blank");
      }}
      role="button"
    >
      {/* Image */}
      <div style={{ position: "relative", width: "100%", height: 200, borderRadius: 6, overflow: "hidden" }}>
        <img
          src={image ? getImageVariantUrl(image, "_compress") : "/sly_0470-sly_0472.jpg"}
          data-original={image}
          alt={title || ""}
          style={{ objectFit: "cover", width: "100%", height: "100%", display: "block" }}
          loading="lazy"
          onError={handleImgError}
        />

        {/* Badges — top left */}
        <div style={{ position: "absolute", top: 12, left: 12, display: "flex", alignItems: "center", gap: 8, zIndex: 10 }}>
          {badges.map((badge, i) => (
            <div
              key={i}
              style={{
                backgroundColor: "#ffffff",
                borderRadius: 20,
                paddingLeft: 10,
                paddingRight: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 23,
                boxShadow: "0px 0px 1px rgba(0,0,0,0.25)",
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  lineHeight: "24px",
                  color: "#01152E",
                  whiteSpace: "nowrap",
                  textTransform: "capitalize",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 500,
                }}
              >
                {badge}
              </span>
            </div>
          ))}
        </div>

        {/* Heart — top right */}
        <div style={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}>
          <img src={heartFilledIcon} alt="" width={23} height={20} />
        </div>
      </div>

      {/* Title */}
      <p
        style={{
          fontSize: 16,
          lineHeight: "24px",
          color: "#01152E",
          marginTop: 12,
          marginBottom: 0,
          fontFamily: "Poppins, sans-serif",
          fontWeight: 600,
        }}
      >
        {title}
      </p>

      {/* Location */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 0 }}>
        <img src={pinIcon} alt="" style={{ width: 8, height: 10, flexShrink: 0 }} />
        <span
          style={{
            fontSize: 12,
            lineHeight: "24px",
            color: "#01152E",
            letterSpacing: 0.24,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 400,
          }}
        >
          {displayLocation}
        </span>
      </div>

      {/* Room stats */}
      <div style={{ display: "flex", alignItems: "center", gap: 24, marginTop: 0 }}>
        {bedrooms != null && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <img src={bedIcon} alt="" style={{ width: 19, height: 14 }} />
            <span
              style={{ fontSize: 12, lineHeight: "24px", color: "#01152E", fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
            >
              {bedrooms}
            </span>
          </div>
        )}
        {bathrooms != null && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <img src={bathIcon} alt="" style={{ width: 14, height: 14 }} />
            <span
              style={{ fontSize: 12, lineHeight: "24px", color: "#01152E", fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
            >
              {bathrooms}
            </span>
          </div>
        )}
        {toilets != null && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <img src={kitchenIcon} alt="" style={{ width: 12, height: 14 }} />
            <span
              style={{ fontSize: 12, lineHeight: "24px", color: "#01152E", fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
            >
              {toilets}
            </span>
          </div>
        )}
        {sleeps != null && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <img src={sleepsIcon} alt="" style={{ width: 18, height: 14 }} />
            <span
              style={{ fontSize: 12, lineHeight: "24px", color: "#01152E", fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
            >
              {sleeps}
            </span>
          </div>
        )}
      </div>

      {/* Description */}
      {descriptionText && (
        <p
          style={{
            fontSize: 10,
            lineHeight: "14px",
            color: "#445162",
            marginTop: 6,
            marginBottom: 6,
            fontFamily: "Roboto, sans-serif",
            fontWeight: 400,
          }}
        >
          {descriptionText.length > 180
            ? descriptionText.slice(0, 180) + "..."
            : descriptionText}
        </p>
      )}

      {/* Amenities section */}
      {amenityList.length > 0 && (
        <div>
          {/* Top separator */}
          <div style={{ width: "100%", height: 1, backgroundColor: "#d6d6d6" }} />

          {/* Amenity items — no flex on this wrapper, use inline-flex children */}
          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
            {visibleAmenities.map((am: { name: string }, i: number) => {
              const icon = getAmenityIcon(am.name);
              return (
                <span
                  key={i}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginRight: i < visibleAmenities.length - 1 || moreCount > 0 ? 20 : 0,
                  }}
                >
                  {icon && <img src={icon} alt="" style={{ width: 16, height: 12 }} />}
                  <span
                    style={{
                      fontFamily: "Manrope, sans-serif",
                      fontWeight: 500,
                      fontSize: 13,
                      lineHeight: "24px",
                      color: "#01152E",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {am.name}
                  </span>
                </span>
              );
            })}
            {moreCount > 0 && (
              <span
                style={{
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: 500,
                  fontSize: 13,
                  lineHeight: "24px",
                  color: "#445162",
                  whiteSpace: "nowrap",
                }}
              >
                +{moreCount} {t("listingStay.more")}
              </span>
            )}
          </div>

          {/* Bottom separator */}
          <div style={{ width: "100%", height: 1, backgroundColor: "#d6d6d6" }} />
        </div>
      )}

      {/* Footer — Price + Book Now */}
      <div style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 40 }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}>
          <span
            style={{ fontSize: 12, lineHeight: "24px", color: "#01152E", fontFamily: "Inter, sans-serif", fontWeight: 600 }}
          >
            &euro;
          </span>
          <span
            style={{ fontSize: 16, lineHeight: "24px", color: "#01152E", fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
          >
            {price}
          </span>
          <span
            style={{ fontSize: 12, lineHeight: "24px", color: "#01152E", fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
          >
            {t("listingStay.perNight")}
          </span>
        </div>
        <button
          onClick={handleBookNow}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 30,
            paddingLeft: 24,
            paddingRight: 24,
            borderRadius: 30,
            backgroundColor: "#13263F",
            cursor: "pointer",
            border: "none",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 12,
              lineHeight: "24px",
              color: "#ffffff",
              whiteSpace: "nowrap",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
            }}
          >
            {t("common.bookNow")}
          </span>
        </button>
      </div>
    </div>
  );
};

export default MapPropertyCard;
